/**
 * DECISIONS 415 step 1 (ARCHITECTURE §5.52 (3), "one read a look"): what a soldier's decision reads, read once a decision
 * rather than at every block that asks. Today it holds the side's knowledge round the unit — `teamKnowledge` under the
 * unit's own options — which the deliberation used to ask for afresh in thirteen places (the same answer each time: the
 * tracks stand after perception's edits, and every other feed is the tick's start).
 *
 * Lazy and kept for one decision only (ai/actionBot.ts makes one a drive, after perception): nothing is read that the
 * decision doesn't ask for, so it costs nothing where it isn't used, and two units never share one. Each view keeps the
 * filter its callers had: the planet's beliefs, and those over the side's confidence floor. The arrays are shared
 * between callers, so they are read-only: a caller that sorts copies first.
 */
import type { KnownEnemy } from './actionKnowledge';

export interface SoldierRead {
  /** Every belief the unit's side holds, in teamKnowledge's order (its own last look, the picture, sightings, marks). */
  known(): readonly KnownEnemy[];
  /** Those on the unit's planet (or with no planet named). */
  knownHere(): readonly KnownEnemy[];
  /** Those on its planet at or over the side's confidence floor (ACTION_PARAMS.rules.teamTrackMinConfidence). */
  knownSure(): readonly KnownEnemy[];
}

/** The read of one decision: `feed` is the unit's teamKnowledge call, made at the first ask and kept. */
export function soldierRead(feed: () => KnownEnemy[], planetIndex: number, minConfidence: number): SoldierRead {
  let all: KnownEnemy[] | null = null, here: KnownEnemy[] | null = null, sure: KnownEnemy[] | null = null;
  const known = () => (all ??= feed());
  const knownHere = () => (here ??= known().filter(k => k.planetIndex === undefined || k.planetIndex === planetIndex));
  const knownSure = () => (sure ??= knownHere().filter(k => k.confidence >= minConfidence));
  return { known, knownHere, knownSure };
}
